<template>
  <div class="guide-summary">
    
    <!-- 加载和错误状态 -->
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    
    <!-- 主内容 -->
    <div v-else-if="journalData" class="journal-container">
      
      <!-- 头部容器 - 居中 -->
      <div class="header-container">
        <!-- 日期区域 -->
        <div class="date-section">
          <img src="@/assets/journey1/date.png" alt="date" class="date-img" />
        </div>
        
        <!-- 标题区域 -->
        <div class="title-section">
          <img src="@/assets/journey1/title.png" alt="title background" class="title-bg" />
          <div class="title-text">{{ journalTitle }}</div>
        </div>
      </div>
      
      <!-- 段落容器 - 支持动态循环显示 -->
      <template v-for="(paragraph, index) in paragraphs" :key="index">
        <div 
          v-if="paragraph && (paragraph.image || paragraph.text)"
          :class="`${getStyleName(index)}-paragraph-container`"
        >
          <!-- 图片容器 -->
          <div :class="`${getStyleName(index)}-photo-container`">
            <img 
              v-if="paragraph.image" 
              :src="paragraph.image" 
              alt="photo content" 
              :class="`${getStyleName(index)}-photo-content`" 
            />
          </div>
          
          <!-- 红点和路径线容器 -->
          <div :class="`${getStyleName(index)}-dot-line-container`">
            <!-- 红点 -->
            <img 
              src="@/assets/journey1/redDot.png" 
              alt="red dot" 
              :class="`${getStyleName(index)}-red-dot`" 
            />
          </div>
          
          <p 
            :class="`${getStyleName(index)}-paragraph-text`" 
            v-if="paragraph.text" 
            :style="getTextStyle(paragraph.textItem)"
          >
            {{ paragraph.text }}
          </p>
        </div>
      </template>
      
      <!-- 结尾段落容器 -->
      <div class="ending-container" v-if="endingText">
        <img src="@/assets/journey1/ending.png" alt="旅程结束" class="ending-image" />
        <div class="ending-text-box">
          <p class="ending-paragraph" :style="getTextStyle(endingTextItem)">{{ endingText }}</p>
        </div>
      </div>
      
      <!-- 其他内容暂时隐藏，待后续添加 -->
    </div>
  </div>
</template>

<script>
export default {
  name: 'GuideSummary',
  data() {
    return {
      loading: false,
      error: null,
      journalData: null,
      contentList: [],
      journalTitle: '',
      imageCounter: 0,
      showBorders: false  // 控制是否显示调试边框
    }
  },
  computed: {
    totalImages() {
      return this.contentList.filter(item => item.tag === 'IMAGE_LINK').length
    },
    
    // 根据showBorders变量返回border样式
    borderStyle() {
      return this.showBorders ? '2px dashed' : 'none'
    },
    
    // 组织段落数据：每个段落包含图片和文本
    // 仅当图片的下一条是正文时才配对，其他情况忽略正文
    // 收集所有图文对，支持超过9个（样式会循环使用）
    paragraphs() {
      const paragraphsArray = []
      
      // 遍历contentList，查找所有图片并检查下一项是否为文本
      for (let i = 0; i < this.contentList.length; i++) {
        const currentItem = this.contentList[i]
        
        // 如果当前项是图片
        if (currentItem.tag === 'IMAGE_LINK') {
          const nextItem = this.contentList[i + 1]
          
          // 检查下一项是否存在且为文本
          if (nextItem && nextItem.tag === 'TEXT' && nextItem.content) {
            // 配对：图片 + 正文
            paragraphsArray.push({
              image: currentItem.content,
              text: nextItem.content,
              textItem: nextItem
            })
            // 跳过下一项（已经配对的文本）
            i++
          } else {
            // 只有图片，没有配对的文本
            paragraphsArray.push({
              image: currentItem.content,
              text: null,
              textItem: null
            })
          }
        }
      }
      
      return paragraphsArray
    },
    
    // 获取结尾文本
    // 查找TAG为ENDING的项作为结尾文本
    endingText() {
      const endingItem = this.contentList.find(item => item.tag === 'ENDING')
      return endingItem ? endingItem.content : null
    },
    
    // 获取结尾文本项（用于样式）
    endingTextItem() {
      return this.contentList.find(item => item.tag === 'ENDING') || null
    }
  },
  created() {
    this.fetchJournalData()
  },
  methods: {
    // 获取段落的样式序号（0-8循环）
    // 例如：索引10会返回1（使用第2个段落的样式）
    getStyleIndex(index) {
      return index % 9
    },
    
    // 获取段落序号的文字表示（用于CSS类名）
    // 0->first, 1->second, 2->third, ..., 8->ninth
    getStyleName(index) {
      const names = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth']
      const styleIndex = this.getStyleIndex(index)
      return names[styleIndex]
    },
    
    async fetchJournalData() {
      const taskId = this.$route.params.xxxxxxx
      
      if (!taskId) {
        this.error = '缺少任务ID'
        return
      }
      
      this.loading = true
      this.error = null
      
      try {
        const response = await fetch(
          `https://api.vrstate.com/vrstate-platform/guide-service/journey/travel-journal/task/${taskId}`
        )
        
        if (!response.ok) {
          throw new Error(`请求失败: ${response.status}`)
        }
        
        const result = await response.json()
        
        if (result.code === 200 && result.data) {
          this.journalData = result.data
          this.parseContent(result.data.content)
        } else {
          this.error = result.message || '获取数据失败'
        }
      } catch (err) {
        this.error = `加载失败: ${err.message}`
        console.error('获取旅游日志失败:', err)
      } finally {
        this.loading = false
      }
    },
    
    parseContent(contentStr) {
      try {
        const contentArray = JSON.parse(contentStr)
        this.contentList = contentArray.sort((a, b) => a.sortOrder - b.sortOrder)
        
        // 提取主标题
        const mainTitle = this.contentList.find(item => item.tag === 'MAIN_TITLE')
        this.journalTitle = mainTitle ? mainTitle.content : '旅行日志'
      } catch (err) {
        console.error('解析内容失败:', err)
        this.error = '内容格式错误'
      }
    },
    
    imageIndex(item) {
      // 计算当前图片是第几张
      const imageItems = this.contentList.filter(i => i.tag === 'IMAGE_LINK')
      return imageItems.findIndex(i => i.sortOrder === item.sortOrder) + 1
    },
    
    getPhotoFrameUrl(index) {
      // 根据索引返回对应的照片框素材
      return require(`@/assets/journey1/pic${index}.png`)
    },
    
    getLineUrl(index) {
      // 根据索引返回对应的路径线素材
      return require(`@/assets/journey1/line${index}.png`)
    },
    
    getTextStyle(item) {
      if (!item) {
        return {}
      }
      
      const style = {
        color: item.color || '#000000'
      }
      
      // 根据size设置字体大小
      if (item.size === 'LARGE') {
        style.fontSize = '24px'
        style.fontWeight = 'bold'
      } else if (item.size === 'MEDIUM') {
        style.fontSize = '14px'
      } else if (item.size === 'SMALL') {
        style.fontSize = '12px'
      }
      
      return style
    }
  }
}
</script>

<style scoped>
/* 调试控制按钮样式 */
.debug-controls {
  position: fixed;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.debug-controls button:hover {
  background: #e0e0e0;
}

.guide-summary {
  width: 100%;
  min-height: 100vh;
  background-image: url('@/assets/journey1/background.png');
  background-size: cover;
  background-position: center top;
  background-repeat: repeat-y;
  padding: 0;
  margin: 0;
  overflow-x: hidden;
}

.loading,
.error {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #333;
}

.error {
  color: #ff4444;
}

/* 主容器 - 作为定位上下文 */
/* PC端样式 (最小1025px) */
@media (min-width: 1025px) {
  .journal-container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    padding-top: 20px;
    position: relative;
    box-sizing: border-box;
  }

  /* 头部容器 - 水平居中 */
  .header-container {
    position: relative;
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 15px;
    /* border: 2px dashed #ff0000; */
  }

  /* 日期区域 - 绝对定位在左上角 */
  .date-section {
    position: absolute;
    top: 40px;
    left: 0px;
    width: 220px;
    z-index: 10;
    /* border: 2px dashed #00ff00; */
  }

  .date-img {
    width: 100%;
    height: auto;
    display: block;
    /* border: 2px dashed #ffff00; */
  }

  /* 标题区域 - 绝对定位 */
  .title-section {
    position: absolute;
    top: 30px;
    left: 80px;
    width: 80%;
    z-index: 5;
    /* border: 2px dashed #0000ff; */
  }

  .title-bg {
    width: 100%;
    height: auto;
    display: block;
    /* border: 2px dashed #ff00ff; */
  }

  .title-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(5deg);
    font-size: 26px;
    font-weight: bold;
    color: #000;
    text-align: center;
    width: 50%;
    line-height: 1.3;
    /*  border: 2px dashed #00ffff;  */
    background-color: transparent;
  }

  /* 第一段落容器 - 居中 */
  .first-paragraph-container {
    width: 400px;
    height: auto;
    margin: 0 auto;
    margin-bottom: 20px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .first-photo-container {
    width: 200px;
    height: 150px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic1.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  /* 图片框样式已合并到photo-container中 */
  .first-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .first-photo-content {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 77%;
    height: 77%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-6deg);
  }

  /* 红点和路径线容器 */
  .dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0;
    left: 40%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .first-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .first-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -25px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }

  .first-paragraph-text {
    font-size: 14px;
    margin-top: 0%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff99; */
    flex: 1;
  }

  /* 第二段落容器 - 居中 */
  .second-paragraph-container {
    width: 400px;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 15px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .second-photo-container {
    width: 200px;
    height: 200px;
    top: -30%;
    left: 0%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic2.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .second-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .second-photo-content {
    position: absolute;
    top: 40%;
    left: 8%;
    width: 85%;
    height: 55%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  /* 红点和路径线容器 */
  .second-dot-line-container {
    position: absolute;
    width: 120px;
    height: 100px;
    top: 0%;
    right: 21%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .second-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .second-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 0px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .second-paragraph-text {
    font-size: 14px;
    margin-top: 5%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第三段落容器 - 居中 */
  .third-paragraph-container {
    width: 400px;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .third-photo-container {
    width: 180px;
    height: 180px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic3.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .third-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .third-photo-content {
    position: absolute;
    top: 8%;
    left: 22%;
    width: 70%;
    height: 70%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-6deg);
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .third-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0px;
    left: 85px;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .third-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .third-path-line {
    position: absolute;
    width: 250px;
    height: auto;
    top: 20px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }

  .third-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第四段落容器 - 居中 */
  .fourth-paragraph-container {
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .fourth-photo-container {
    width: 150px;
    height:180px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic4.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .fourth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .fourth-photo-content {
    position: absolute;
    top: 4%;
    left: 4%;
    width: 75%;
    height: 67%;
    object-fit: cover;
    z-index: -1;
    /*  border: 2px dashed #00ffff;  */
  }

  /* 红点和路径线容器 */
  .fourth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    right: 17%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .fourth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fourth-path-line {
    position: absolute;
    width: 390px;
    height: auto;
    top: 40px;
    right: -20px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .fourth-paragraph-text {
    font-size: 14px;
    margin-top: 0px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第五段落容器 - 居中 */
  .fifth-paragraph-container {
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .fifth-photo-container {
    width: 200px;
    height: 150px;
    top: -5%;
    left: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic5.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .fifth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .fifth-photo-content {
    position: absolute;
    top: 12%;
    left: 10%;
    width: 80%;
    height: 75%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-9deg);
  }

  /* 红点和路径线容器 */
  .fifth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 10%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .fifth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fifth-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 40px;
    left: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .fifth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第六段落容器 - 居中 */
  .sixth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 10;
  }

  /* 图片容器 */
  .sixth-photo-container {
    width: 150px;
    height: 150px;
    right: 10%;
    top: -5%;
    position: relative;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic6.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 5;
  }

  .sixth-photo-content {
    position: absolute;
    top: 2%;
    left: 10%;
    width: 80%;
    height: 60%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(9deg);
    margin-top: 10px;
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .sixth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    right: 25%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .sixth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .sixth-path-line {
    position: absolute;
    width: 280px;
    height: auto;
    top: -10%;
    right: -20%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .sixth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-left: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第七段落容器 - 居中 */
  .seventh-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .seventh-photo-container {
    width: 240px;
    height: 180px;
    position: relative;
    top: -30%;
    left: -7%;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic7.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }

  .seventh-photo-frame {
    width: 130%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .seventh-photo-content {
    position: absolute;
    top: 24%;
    left: 40%;
    width: 50%;
    height: 65%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-15deg);
  }

  /* 红点和路径线容器 */
  .seventh-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    left: 12%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .seventh-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .seventh-path-line {
    position: absolute;
    width: 240px;
    height: auto;
    top: 5%;
    left: 0%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .seventh-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-right: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第八段落容器 - 居中 */
  .eighth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .eighth-photo-container {
    width: 200px;
    height: 225px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic8.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }

  .eighth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .eighth-photo-content {
    position: absolute;
    top: 10%;
    left: 14%;
    width: 55%;
    height: 50%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(11deg);
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .eighth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 5%;
    right: 28%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .eighth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .eighth-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -20px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .eighth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-left: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第九段落容器 - 居中 */
  .ninth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .ninth-photo-container {
    width: 205px;
    height: 150px;
    position: relative;
    z-index: 5;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic9.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .ninth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .ninth-photo-content {
    position: absolute;
    top: 7%;
    left: 9%;
    width: 85%;
    height: 85%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  /* 红点和路径线容器 */
  .ninth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 26%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .ninth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  .ninth-paragraph-text {
    font-size: 14px;
    margin-top: 15%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 结尾段落容器 */
  .ending-container {
    width: 400px;
    margin: 0 auto;
    margin-bottom: 50px;
    text-align: center;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    position: relative;
  }

  .ending-image {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
    /* border: 2px dashed #00ff99; */
  }

  .ending-text-box {
    position: absolute;
    top: 50%;
    right: 10%;
    transform: translateY(-50%);
    width: 35%;
    padding: 15px;
    /* border: 2px dashed #ff00ff; */
    background-color: transparent;
    box-sizing: border-box;
  }

  .ending-paragraph {
    margin: 0;
    line-height: 1.6;
    font-size: 14px;
    color: #333;
    text-align: left;
    /*  border: 2px dashed #00ffff;  */
  }
}

/* 平板设备 (768px - 1024px) */
@media (max-width: 1024px) and (min-width: 769px) {
  .journal-container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    padding-top: 20px;
    position: relative;
    box-sizing: border-box;
  }

  /* 头部容器 - 水平居中 */
  .header-container {
    position: relative;
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 15px;
    /* border: 2px dashed #ff0000; */
  }

  /* 日期区域 - 绝对定位在左上角 */
  .date-section {
    position: absolute;
    top: 40px;
    left: 0px;
    width: 220px;
    z-index: 10;
    /* border: 2px dashed #00ff00; */
  }

  .date-img {
    width: 100%;
    height: auto;
    display: block;
    /* border: 2px dashed #ffff00; */
  }

  /* 标题区域 - 绝对定位 */
  .title-section {
    position: absolute;
    top: 30px;
    left: 80px;
    width: 80%;
    z-index: 5;
    /* border: 2px dashed #0000ff; */
  }

  .title-bg {
    width: 100%;
    height: auto;
    display: block;
    /* border: 2px dashed #ff00ff; */
  }

  .title-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(5deg);
    font-size: 26px;
    font-weight: bold;
    color: #000;
    text-align: center;
    width: 50%;
    line-height: 1.3;
    /*  border: 2px dashed #00ffff;  */
    background-color: transparent;
  }

  /* 第一段落容器 - 居中 */
  .first-paragraph-container {
    width: 400px;
    height: auto;
    margin: 0 auto;
    margin-bottom: 20px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .first-photo-container {
    width: 200px;
    height: 150px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic1.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  /* 图片框样式已合并到photo-container中 */
  .first-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .first-photo-content {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 77%;
    height: 77%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-6deg);
  }

  /* 红点和路径线容器 */
  .dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0;
    left: 40%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .first-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .first-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -25px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }

  .first-paragraph-text {
    font-size: 14px;
    margin-top: 0%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff99; */
    flex: 1;
  }

  /* 第二段落容器 - 居中 */
  .second-paragraph-container {
    width: 400px;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 15px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .second-photo-container {
    width: 200px;
    height: 200px;
    top: -30%;
    left: 0%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic2.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .second-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .second-photo-content {
    position: absolute;
    top: 40%;
    left: 8%;
    width: 85%;
    height: 55%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  /* 红点和路径线容器 */
  .second-dot-line-container {
    position: absolute;
    width: 120px;
    height: 100px;
    top: 0%;
    right: 21%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .second-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .second-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 0px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .second-paragraph-text {
    font-size: 14px;
    margin-top: 5%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第三段落容器 - 居中 */
  .third-paragraph-container {
    width: 400px;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .third-photo-container {
    width: 180px;
    height: 180px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic3.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .third-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .third-photo-content {
    position: absolute;
    top: 8%;
    left: 22%;
    width: 70%;
    height: 70%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-6deg);
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .third-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0px;
    left: 85px;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .third-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .third-path-line {
    position: absolute;
    width: 250px;
    height: auto;
    top: 20px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }

  .third-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第四段落容器 - 居中 */
  .fourth-paragraph-container {
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .fourth-photo-container {
    width: 150px;
    height:180px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic4.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .fourth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .fourth-photo-content {
    position: absolute;
    top: 4%;
    left: 4%;
    width: 75%;
    height: 67%;
    object-fit: cover;
    z-index: -1;
    /*  border: 2px dashed #00ffff;  */
  }

  /* 红点和路径线容器 */
  .fourth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    right: 17%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .fourth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fourth-path-line {
    position: absolute;
    width: 390px;
    height: auto;
    top: 40px;
    right: -20px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .fourth-paragraph-text {
    font-size: 14px;
    margin-top: 0px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第五段落容器 - 居中 */
  .fifth-paragraph-container {
    width: 400px;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .fifth-photo-container {
    width: 200px;
    height: 150px;
    top: -5%;
    left: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic5.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .fifth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .fifth-photo-content {
    position: absolute;
    top: 12%;
    left: 10%;
    width: 80%;
    height: 75%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-9deg);
  }

  /* 红点和路径线容器 */
  .fifth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 10%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .fifth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fifth-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 40px;
    left: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .fifth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第六段落容器 - 居中 */
  .sixth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 10;
  }

  /* 图片容器 */
  .sixth-photo-container {
    width: 150px;
    height: 150px;
    right: 10%;
    top: -5%;
    position: relative;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic6.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 5;
  }

  .sixth-photo-content {
    position: absolute;
    top: 2%;
    left: 10%;
    width: 80%;
    height: 60%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(9deg);
    margin-top: 10px;
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .sixth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    right: 25%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .sixth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .sixth-path-line {
    position: absolute;
    width: 280px;
    height: auto;
    top: -10%;
    right: -20%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .sixth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-left: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第七段落容器 - 居中 */
  .seventh-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .seventh-photo-container {
    width: 240px;
    height: 180px;
    position: relative;
    top: -30%;
    left: -7%;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic7.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }

  .seventh-photo-frame {
    width: 130%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .seventh-photo-content {
    position: absolute;
    top: 24%;
    left: 40%;
    width: 50%;
    height: 65%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-15deg);
  }

  /* 红点和路径线容器 */
  .seventh-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    left: 12%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .seventh-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .seventh-path-line {
    position: absolute;
    width: 240px;
    height: auto;
    top: 5%;
    left: 0%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .seventh-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-right: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第八段落容器 - 居中 */
  .eighth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .eighth-photo-container {
    width: 200px;
    height: 225px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic8.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }

  .eighth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .eighth-photo-content {
    position: absolute;
    top: 10%;
    left: 14%;
    width: 55%;
    height: 50%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(11deg);
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .eighth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 5%;
    right: 28%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .eighth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .eighth-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -20px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .eighth-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-left: 10%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 第九段落容器 - 居中 */
  .ninth-paragraph-container {
    width: 400px;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    position: relative;
    z-index: 1;
  }

  /* 图片容器 */
  .ninth-photo-container {
    width: 205px;
    height: 150px;
    position: relative;
    z-index: 5;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic9.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .ninth-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .ninth-photo-content {
    position: absolute;
    top: 7%;
    left: 9%;
    width: 85%;
    height: 85%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  /* 红点和路径线容器 */
  .ninth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 26%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .ninth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  .ninth-paragraph-text {
    font-size: 14px;
    margin-top: 15%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    flex: 1;
  }

  /* 结尾段落容器 */
  .ending-container {
    width: 400px;
    margin: 0 auto;
    margin-bottom: 50px;
    text-align: center;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    position: relative;
  }

  .ending-image {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
    /* border: 2px dashed #00ff99; */
  }

  .ending-text-box {
    position: absolute;
    top: 50%;
    right: 10%;
    transform: translateY(-50%);
    width: 35%;
    padding: 15px;
    /* border: 2px dashed #ff00ff; */
    background-color: transparent;
    box-sizing: border-box;
  }

  .ending-paragraph {
    margin: 0;
    line-height: 1.6;
    font-size: 14px;
    color: #333;
    text-align: left;
    /*  border: 2px dashed #00ffff;  */
  }
}

/* 移动端 (最大768px) */
@media (max-width: 768px) {
  .journal-container {
    padding: 0px;
    padding-top: 0px;
  }
  
  .header-container {
    position: relative;
    width: 90%;
    height: 180px;
    margin: 0 auto;
    margin-bottom: 3%;
    display: block;
  }
  
  /* 移动端仿PC版绝对定位布局 */
  .date-section {
    position: absolute;
    top: 20%;
    left: 5%;
    width: 45%;
    z-index: 10;
    /* border: 2px dashed #00ff00; */
  }
  
  .date-img {
    width: 100%;
    height: auto;
    display: block;
    position: relative;
    z-index: 5;
    /* border: 2px dashed #ffff00; */
  }
  
  .title-section {
    position: absolute;
    top: 15%;
    left: 20%;
    width: 60%;
    z-index: 5;
    /* border: 2px dashed #0000ff; */
  }
  .title-bg {
    width: 120%;
    height: auto;
    display: block;
    /* border: 2px dashed #ff00ff; */
  }
  .title-text {
    position: absolute;
    top: 50%;
    left: 60%;
    transform: translate(-50%, -50%) rotate(5deg);
    font-size: 20px;
    font-weight: bold;
    color: #000;
    text-align: center;
    width: 70%;
    line-height: 1.3;
    background-color: transparent;
    /*  border: 2px dashed #00ffff;  */
  }
  
  .first-paragraph-container {
    width: 95%;
    height: auto;
    margin: 0 auto;
    margin-bottom: 30px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .first-photo-container {
    width: 200px;
    height: 150px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic1.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  /* 图片框样式已合并到photo-container中 */
  .first-photo-frame {
    width: 100%;
    height: auto;
    display: block;
    border: 2px dashed #ff9900;
  }

  .first-photo-content {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 77%;
    height: 77%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-6deg);
  }

  /* 红点和路径线容器 */
  .dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0;
    left: 40%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .first-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .first-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -25px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }

  .first-paragraph-text {
    font-size: 14px;
    margin-top: 5%;
    margin-right: 5%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff99; */
  }
  
  .second-paragraph-container {
    width: 95%;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 30px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .second-photo-container {
    width: 200px;
    height: 200px;
    top: -40%;
    left: 0%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic2.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  .second-photo-content {
    position: absolute;
    top: 40%;
    left: 8%;
    width: 85%;
    height: 55%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  .second-dot-line-container {
    width: 120px;
    height: 100px;
    position: absolute;
    top: 10%;
    right: 30%;
  }

  /* 红点 */
  .second-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .second-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 0px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .second-paragraph-text {
    font-size: 14px;
    margin-top: 7%;
    margin-left: 8%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .third-paragraph-container {
    width: 95%;
    height: 120px;
    margin: 0 auto;
    margin-bottom: 30px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .third-photo-container {
    width: 180px;
    height: 180px;
    position: relative;
    /* border: 2px dashed #9900ff; */
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic3.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  .third-photo-content {
    position: absolute;
    top: 8%;
    left: 22%;
    width: 70%;
    height: 70%;
    /*  border: 2px dashed #00ffff;  */
    object-fit: cover;
    transform: rotate(-6deg);
    z-index: -1;
  }
  
  /* 红点和路径线容器 */
  .third-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0px;
    left: 85px;
    /* border: 2px dashed #ff00ff; */
  }
  
  /* 红点 */
  .third-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }
  
  /* 路径线 */
  .third-path-line {
    position: absolute;
    width: 250px;
    height: auto;
    top: 20px;
    left: 0px;
    z-index: 0;
    /* border: 2px dashed #00ff00; */
  }
  
  .third-paragraph-text {
    font-size: 14px;
    margin-top: 10%;
    margin-left: 0%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .fourth-paragraph-container {
    width: 95%;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 20px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .fourth-photo-container {
    width: 150px;
    height:180px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic4.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  .fourth-photo-content {
    position: absolute;
    top: 4%;
    left: 4%;
    width: 75%;
    height: 67%;
    object-fit: cover;
    z-index: -1;
    /*  border: 2px dashed #00ffff;  */
  }
  
  /* 红点和路径线容器 */
  .fourth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    right: 17%;
    /* border: 2px dashed #ff00ff; */
  }
  
  /* 红点 */
  .fourth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fourth-path-line {
    position: absolute;
    width: 340px;
    height: auto;
    top: 0%;
    right: -17%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }
  
  .fourth-paragraph-text {
    font-size: 14px;
    margin-top: 15%;
    margin-left: 7%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .fifth-paragraph-container {
    width: 95%;
    height: 200px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .fifth-photo-container {
    width: 200px;
    height: 150px;
    top: -5%;
    left: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic5.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  .fifth-photo-content {
    position: absolute;
    top: 12%;
    left: 10%;
    width: 80%;
    height: 75%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-9deg);
  }
  
  /* 红点和路径线容器 */
  .fifth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 10%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .fifth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .fifth-path-line {
    position: absolute;
    width: 260px;
    height: auto;
    top: 40px;
    left: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }
  
  .fifth-paragraph-text {
    font-size: 14px;
    margin-top: 3%;
    margin-left: 5%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .sixth-paragraph-container {
    width: 95%;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 20px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 10;
    /* border: 2px dashed #ff6600; */
  }
  
  .sixth-photo-container {
    width: 150px;
    height: 150px;
    right: 5%;
    top: -5%;
    position: relative;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic6.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 5;
  }

  .sixth-photo-content {
    position: absolute;
    top: 2%;
    left: 10%;
    width: 80%;
    height: 60%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(9deg);
    margin-top: 10px;
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .sixth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    right: 25%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .sixth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .sixth-path-line {
    position: absolute;
    width: 280px;
    height: auto;
    top: -10%;
    right: -20%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  
  .sixth-paragraph-text {
    font-size: 14px;
    margin-top: 6%;
    margin-right: 2%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }

  .seventh-paragraph-container {
    width: 95%;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 0px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    /* border: 2px dashed #ff6600; */
    z-index: 1;
  }
  
  .seventh-photo-container {
    width: 240px;
    height: 180px;
    position: relative;
    top: -30%;
    left: -7%;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic7.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }
  
  .seventh-photo-content {
    position: absolute;
    top: 24%;
    left: 40%;
    width: 50%;
    height: 65%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(-15deg);
  }

  /* 红点和路径线容器 */
  .seventh-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: -5%;
    left: 12%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .seventh-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .seventh-path-line {
    position: absolute;
    width: 240px;
    height: auto;
    top: 5%;
    left: 0%;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }

  .seventh-paragraph-text {
    font-size: 14px;
    margin-top: 10px;
    margin-right: 15%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .eighth-paragraph-container {
    width: 95%;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 40px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
  }
  
  .eighth-photo-container {
    width: 200px;
    height: 225px;
    top: 5%;
    position: relative;
    z-index: 5;
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic8.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    /* border: 2px dashed #9900ff; */
  }

  .eighth-photo-content {
    position: absolute;
    top: 10%;
    left: 14%;
    width: 55%;
    height: 50%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(11deg);
    z-index: -1;
  }

  /* 红点和路径线容器 */
  .eighth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 5%;
    right: 28%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .eighth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    right: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }

  /* 路径线 */
  .eighth-path-line {
    position: absolute;
    width: 200px;
    height: auto;
    top: -20px;
    right: 0px;
    z-index: 3;
    /* border: 2px dashed #00ff00; */
  }
  
  .eighth-paragraph-text {
    font-size: 14px;
    margin-top: 10%;
    margin-left: 15%;
    max-width: 40%;
    line-height: 1.8;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #00ff00; */
  }
  
  .ninth-paragraph-container {
    width: 95%;
    height: 160px;
    margin: 0 auto;
    margin-bottom: 20px;
    padding: 0px;
    background-color: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 5px;
    position: relative;
    z-index: 1;
    /* border: 2px dashed #ff6600; */
  }
  
  .ninth-photo-container {
    width: 205px;
    height: 150px;
    position: relative;
    z-index: 5;
    /* border: 2px dashed #9900ff; */
    /* 合并了photo-frame的样式 */
    background-image: url('@/assets/journey1/pic9.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  
  .ninth-photo-content {
    position: absolute;
    top: 7%;
    left: 9%;
    width: 85%;
    height: 85%;
    object-fit: cover;
    /*  border: 2px dashed #00ffff;  */
    transform: rotate(3deg);
  }

  /* 红点和路径线容器 */
  .ninth-dot-line-container {
    position: absolute;
    width: 180px;
    height: 150px;
    top: 0%;
    left: 26%;
    /* border: 2px dashed #ff00ff; */
  }

  /* 红点 */
  .ninth-red-dot {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0px;
    left: 0px;
    z-index: 7;
    /* border: 2px dashed #ff0000; */
  }
  
  .ninth-paragraph-text {
    font-size: 14px;
    margin-top: 15%;
    margin-right: 5%;
    line-height: 1.8;  
    max-width: 40%;
    color: #333;
    text-align: justify;
    /* border: 2px dashed #ff0000; */
  }
  
  /* 结尾段落容器 */
  .ending-container {
    width: 400px;
    margin: 0 auto;
    margin-bottom: 0px;
    text-align: center;
    /* border: 2px dashed #ff6600; */
    background-color: transparent;
    box-sizing: border-box;
    position: relative;
  }

  .ending-image {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
    /* border: 2px dashed #00ff99; */
  }

  .ending-text-box {
    position: absolute;
    top: 6%;
    right: 2%;
    width: 45%;
    padding: 15px;
    /* border: 2px dashed #ff00ff; */
    background-color: transparent;
    box-sizing: border-box;
  }

  .ending-paragraph {
    margin: 0;
    line-height: 1.6;
    font-size: 14px;
    color: #333;
    text-align: left;
    /*  border: 2px dashed #00ffff;  */
  }
}

</style>
